import React from 'react';
import { Button, Checkbox, Classes, Drawer, Icon, NonIdealState, Tab, Tabs, Tag } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { RequirementCoverage, VerificationOutcome } from '../api/cppmodelApi';
import { describeCoverage, formatSeconds, getCoverageInterval, requirementHighlightId, ruleHighlightId, sortOutcomes } from './verification';

export type RunReportTab = 'verification' | 'requirements';

// Violations listed per rule before the rest are summarized.
const MAX_LISTED_VIOLATIONS = 12;

interface RunReportDrawerProps {
    isOpen: boolean;
    tab: RunReportTab;
    onTabChange: (tab: RunReportTab) => void;
    onClose: () => void;
    verification?: Record<string, VerificationOutcome>;
    requirements?: Record<string, RequirementCoverage>;
    // Highlight ids drawn on the 2D graph.
    shownOnGraph: Set<string>;
    onToggleShown: (id: string) => void;
    // Zoom the 2D graph to an interval (s).
    onFocus: (interval: [number | null, number | null]) => void;
}

const STATUS_ICONS = {
    pass: IconNames.TickCircle,
    fail: IconNames.Error,
    error: IconNames.WarningSign,
};

const formatValue = (value: unknown): string => {
    if (typeof value === 'number') {
        return `${Number(value.toPrecision(6))}`;
    }

    return typeof value === 'string' ? value : JSON.stringify(value);
};

const formatInterval = ([from, to]: [number | null, number | null]) => (
    `${from === null ? '−∞' : formatSeconds(from)} – ${formatSeconds(to)} s`
);

const RuleCard: React.FC<{
    name: string;
    outcome: VerificationOutcome;
    isShown: boolean;
    onToggleShown: () => void;
    onFocus: (interval: [number | null, number | null]) => void;
}> = ({ name, outcome, isShown, onToggleShown, onFocus }) => {
    const margins = Object.entries(outcome.margins);
    const metrics = Object.entries(outcome.metrics);
    const listed = outcome.violations.slice(0, MAX_LISTED_VIOLATIONS);

    return (
        <li className={`cm-report-item is-${outcome.status}`}>
            <div className="cm-report-item-header">
                <Icon icon={STATUS_ICONS[outcome.status]} className="cm-report-status" />
                <span className="cm-report-name">{name}</span>
                <Tag minimal round>{outcome.kind}</Tag>
            </div>
            {outcome.explanation && <p className="cm-report-explanation">{outcome.explanation}</p>}
            {(margins.length > 0 || metrics.length > 0) && (
                <dl className="cm-report-values">
                    {margins.map(([key, value]) => (
                        <React.Fragment key={`margin-${key}`}>
                            <dt>margin {key}</dt>
                            <dd className={value !== null && value < 0 ? 'is-negative' : undefined}>
                                {value === null ? '–' : `${value > 0 ? '+' : ''}${formatValue(value)}`}
                            </dd>
                        </React.Fragment>
                    ))}
                    {metrics.map(([key, value]) => (
                        <React.Fragment key={`metric-${key}`}>
                            <dt>{key}</dt>
                            <dd>{formatValue(value)}</dd>
                        </React.Fragment>
                    ))}
                </dl>
            )}
            {outcome.violations.length > 0 && (
                <div className="cm-report-violations">
                    <Checkbox
                        className="cm-report-graph-toggle"
                        checked={isShown}
                        onChange={onToggleShown}
                        label={`Show ${outcome.violations.length} violation${outcome.violations.length === 1 ? '' : 's'} on the graph`}
                    />
                    <div className="cm-report-intervals">
                        {listed.map((interval, index) => (
                            <Button
                                key={index}
                                small
                                minimal
                                icon={IconNames.ZoomToFit}
                                title="Zoom the 2D graph to this interval"
                                onClick={() => onFocus(interval)}
                            >
                                {formatInterval(interval)}
                            </Button>
                        ))}
                        {outcome.violations.length > listed.length && (
                            <span className="cm-report-more">and {outcome.violations.length - listed.length} more</span>
                        )}
                    </div>
                </div>
            )}
        </li>
    );
};

const VerificationPanel: React.FC<Pick<RunReportDrawerProps, 'verification' | 'shownOnGraph' | 'onToggleShown' | 'onFocus'>> = ({
    verification,
    shownOnGraph,
    onToggleShown,
    onFocus,
}) => {
    if (!verification) {
        return <NonIdealState icon={IconNames.Endorsed} title="No verification rules" description="This run created no verification rules." />;
    }

    const outcomes = sortOutcomes(verification);
    const passed = outcomes.filter(([, outcome]) => outcome.pass).length;

    return (
        <>
            <p className="cm-report-summary">{passed} of {outcomes.length} rules passed at the end of the run.</p>
            <ul className="cm-report-list">
                {outcomes.map(([name, outcome]) => (
                    <RuleCard
                        key={name}
                        name={name}
                        outcome={outcome}
                        isShown={shownOnGraph.has(ruleHighlightId(name))}
                        onToggleShown={() => onToggleShown(ruleHighlightId(name))}
                        onFocus={onFocus}
                    />
                ))}
            </ul>
        </>
    );
};

const RequirementsPanel: React.FC<Pick<RunReportDrawerProps, 'requirements' | 'shownOnGraph' | 'onToggleShown' | 'onFocus'>> = ({
    requirements,
    shownOnGraph,
    onToggleShown,
    onFocus,
}) => {
    if (!requirements) {
        return <NonIdealState icon={IconNames.Document} title="No requirements" description="This run named no requirements." />;
    }

    const entries = Object.entries(requirements).sort(([a], [b]) => a.localeCompare(b));

    return (
        <>
            <p className="cm-report-summary">
                The run addresses {entries.length} requirement{entries.length === 1 ? '' : 's'}. Whether they are met is the run's own result.
            </p>
            <ul className="cm-report-list">
                {entries.map(([id, coverage]) => {
                    const interval = getCoverageInterval(coverage);

                    return (
                        <li key={id} className="cm-report-item">
                            <div className="cm-report-item-header">
                                <Icon icon={IconNames.Document} className="cm-report-status" />
                                <span className="cm-report-name">{id}</span>
                                {interval && !coverage.whole_run && (
                                    <Button
                                        small
                                        minimal
                                        icon={IconNames.ZoomToFit}
                                        title="Zoom the 2D graph to the steps that name it"
                                        aria-label={`Zoom to ${id}`}
                                        onClick={() => onFocus(interval)}
                                    />
                                )}
                            </div>
                            <p className="cm-report-explanation">{describeCoverage(coverage)}</p>
                            {interval && !coverage.whole_run && (
                                <Checkbox
                                    className="cm-report-graph-toggle"
                                    checked={shownOnGraph.has(requirementHighlightId(id))}
                                    onChange={() => onToggleShown(requirementHighlightId(id))}
                                    label="Show on the graph"
                                />
                            )}
                        </li>
                    );
                })}
            </ul>
        </>
    );
};

// Verification outcomes and requirement coverage of the shown run. Without a
// backdrop, so the graph stays usable next to it.
export const RunReportDrawer: React.FC<RunReportDrawerProps> = ({ isOpen, tab, onTabChange, onClose, ...panelProps }) => (
    <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title="Run report"
        icon={IconNames.Clipboard}
        size={420}
        hasBackdrop={false}
        canOutsideClickClose={false}
        enforceFocus={false}
        autoFocus={false}
        className="cm-report-drawer"
    >
        <div className={`${Classes.DRAWER_BODY} cm-report-body`}>
            <Tabs id="cm-run-report" selectedTabId={tab} onChange={(id) => onTabChange(id as RunReportTab)}>
                <Tab
                    id="verification"
                    title="Verification"
                    tagContent={panelProps.verification ? Object.keys(panelProps.verification).length : undefined}
                    panel={<VerificationPanel {...panelProps} />}
                />
                <Tab
                    id="requirements"
                    title="Requirements"
                    tagContent={panelProps.requirements ? Object.keys(panelProps.requirements).length : undefined}
                    panel={<RequirementsPanel {...panelProps} />}
                />
            </Tabs>
        </div>
    </Drawer>
);
